import { Camera, KeyRound, MessagesSquare, Zap } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";

const VALUES = [
  {
    icon: Zap,
    title: "Fast replies",
    detail:
      "Quick answers during business hours — you won't wonder where things stand.",
  },
  {
    icon: MessagesSquare,
    title: "Plain English",
    detail: "No jargon, no 40-page reports. Just clear answers.",
  },
  {
    icon: KeyRound,
    title: "You own your website and your domain",
    detail: "Your domain is registered in your name, and the site is yours.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Zach | Web Pilot"
        description="Meet Zachary Laboyteaux, the owner of Web Pilot. Plain English, fast replies, and a website you own — built for home service pros near Indianapolis."
      />

      <section className="bg-navy py-16 sm:py-20" data-testid="about-hero">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-skyblue">
              About
            </p>
            <h1
              data-testid="about-heading"
              className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Hi, I'm Zach.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
              I run Web Pilot, a one-person web design and automation shop near
              Indianapolis.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <Reveal>
            <div
              data-testid="about-photo-slot"
              className="flex aspect-[4/5] w-full max-w-sm flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-offwhite p-8 text-center"
            >
              <Camera className="h-8 w-8 text-slate-400" aria-hidden="true" />
              <p className="mt-4 font-display text-base font-bold text-navy">
                Zach's photo goes here
              </p>
              <p className="mt-1 text-sm text-slate-500">
                A real photo, not a stock headshot.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-5 text-lg leading-relaxed text-slate-600">
              <p>
                I'm Zachary Laboyteaux. I live near Indianapolis, and I work with
                home service businesses — roofing, HVAC, plumbing, landscaping,
                pressure washing, and pest control.
              </p>
              <p>
                I started Web Pilot after seeing good tradespeople lose jobs to
                missed calls and outdated websites. You do great work. Your phone
                and your website should help you win the next job, not lose it.
              </p>
              <p>
                When you work with Web Pilot, you work directly with me — not an
                account manager. You text me, I answer.
              </p>
              <p>
                And Web Pilot is new. That's exactly why previews are free. I'd
                rather show you real work than ask you to take my word for it.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-offwhite py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <h2
              data-testid="about-values-heading"
              className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl"
            >
              How I work
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col gap-4 rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-skyblue-soft">
                    <v.icon className="h-5 w-5 text-skyblue" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-lg font-bold leading-snug text-navy">
                    {v.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-600">
                    {v.detail}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Let's build your preview." buttonLabel="Get a Free Preview" />
    </>
  );
}
